import { useMemo, useState } from 'react'
import { Check, Copy, KeyRound, Plus, Send, UserX } from 'lucide-react'
import { foutTekst, vraag } from '../lib/api'
import { datum } from '../lib/opmaak'
import type { Ik } from '../lib/typen'
import { ROLLEN, ROLNAAM, alleVestigingen, heeftPin, kanInloggen, magRolBeheren, type Rol } from '../../supabase/functions/_gedeeld/regels.ts'
import { Lade } from '../onderdelen/Lade'
import { Laden, useLaad, type Sessie } from './Beheer'

export function Mensen({ s }: { s: Sessie }) {
  const { data, fout, laad, zet } = useLaad<{ personen: Ik[]; pinLengte: number }>('personen')
  const [bewerk, zetBewerk] = useState<Partial<Ik> | null>(null)
  const [link, zetLink] = useState<{ link: string; naam: string; verloopt: string } | null>(null)
  const [q, zetQ] = useState('')
  const [uitDienst, zetUitDienst] = useState(false)
  const vn = new Map(s.vestigingen.map((v) => [v.id, v.naam.replace(/^Truckwash\s*/i, '')]))

  const lijst = useMemo(() => (data?.personen ?? [])
    .filter((p) => p.actief !== uitDienst)
    .filter((p) => !q || `${p.naam} ${p.email} ${p.mobiel}`.toLowerCase().includes(q.toLowerCase())), [data, q, uitDienst])

  const uitnodigen = async (p: Ik) => {
    try { zetLink(await vraag('persoon-uitnodigen', { id: p.id })) } catch (e) { alert(foutTekst(e)) }
  }

  return (
    <>
      <div className="pagina-kop">
        <h2>Medewerkers</h2>
        <button className="knop geel" onClick={() => zetBewerk({ rol: 'medewerker', vestigingen: [], actief: true })}><Plus size={19} />Nieuwe medewerker</button>
      </div>
      <div className="filter-balk">
        <input className="invoer" type="search" placeholder="Zoek op naam, e-mail of mobiel" value={q} onChange={(e) => zetQ(e.target.value)} />
      </div>
      <div className="chips">
        <button className="chip" aria-pressed={!uitDienst} onClick={() => zetUitDienst(false)}>In dienst</button>
        <button className="chip" aria-pressed={uitDienst} onClick={() => zetUitDienst(true)}>Uit dienst</button>
      </div>
      {!data ? <Laden fout={fout} opnieuw={laad} /> : (
        <div className="kaart" style={{ padding: '4px 0' }}>
          <table className="tabel kaartjes">
            <thead><tr><th>Naam</th><th>Rol</th><th>Vestigingen</th><th>PIN</th><th>Inlog</th></tr></thead>
            <tbody>
              {lijst.map((p) => (
                <tr key={p.id} className="klikbaar" onClick={() => zetBewerk(p)}>
                  <td className="hoofd">{p.naam}</td>
                  <td data-l="Rol">{ROLNAAM[p.rol]}</td>
                  <td data-l="Vestigingen">{alleVestigingen(p.rol) ? 'Alle' : p.vestigingen.map((v) => vn.get(v)).filter(Boolean).join(', ') || '—'}</td>
                  <td data-l="PIN">{heeftPin(p.rol) ? (p.heeftPin ? <span className="badge open"><Check size={13} />Gezet</span> : <span className="badge let">Geen</span>) : <span className="stil">n.v.t.</span>}</td>
                  <td data-l="Inlog">{kanInloggen(p.rol) ? (p.heeftWachtwoord ? <span className="badge open">Actief</span> : p.uitnodigingOpen ? <span className="badge geel">Uitgenodigd</span> : <span className="badge let">Nog niet</span>) : <span className="stil">n.v.t.</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!lijst.length && <div className="leeg-staat">Niemand gevonden.</div>}
        </div>
      )}
      <Lade titel={bewerk?.id ? bewerk.naam ?? '' : 'Nieuwe medewerker'} open={!!bewerk} sluit={() => zetBewerk(null)} breed
        kinderen={bewerk && data && <PersoonFormulier p={bewerk} s={s} lengte={data.pinLengte}
          uitnodigen={uitnodigen}
          klaar={(r) => { zet(r); zetBewerk(null) }} />} />
      <Lade titel={`Uitnodiging voor ${link?.naam ?? ''}`} open={!!link} sluit={() => { zetLink(null); laad() }} kinderen={link && <Uitnodiging {...link} />} />
    </>
  )
}

function PersoonFormulier({ p, s, lengte, klaar, uitnodigen }: {
  p: Partial<Ik>; s: Sessie; lengte: number; klaar: (r: { personen: Ik[]; pinLengte: number }) => void; uitnodigen: (p: Ik) => void
}) {
  const [f, zet] = useState({ naam: p.naam ?? '', mobiel: p.mobiel ?? '', email: p.email ?? '', rol: (p.rol ?? 'medewerker') as Rol, pin: '' })
  const [vest, zetVest] = useState<string[]>(p.vestigingen ?? [])
  const [actief, zetActief] = useState(p.actief !== false)
  const [fout, zetFout] = useState<string | null>(null)
  const [bezig, zetBezig] = useState(false)
  const rollen = ROLLEN.filter((r) => magRolBeheren(s.ik.rol, r))
  const magWijzigen = !p.rol || magRolBeheren(s.ik.rol, p.rol)
  const zelf = p.id === s.ik.id

  const opslaan = async (nieuwActief = actief) => {
    zetBezig(true); zetFout(null)
    try {
      klaar(await vraag('persoon-opslaan', { id: p.id, ...f, vestigingen: vest, actief: nieuwActief }))
    } catch (e) { zetFout(foutTekst(e)) } finally { zetBezig(false) }
  }

  if (!magWijzigen) return <div className="melding let">{ROLNAAM[p.rol as Rol]} kan alleen door MT gewijzigd worden.</div>

  return (
    <form className="formulier" onSubmit={(e) => { e.preventDefault(); opslaan() }}>
      <div className="twee-kol">
        <label className="veld"><span>Naam</span><input value={f.naam} onChange={(e) => zet({ ...f, naam: e.target.value })} autoComplete="off" required /></label>
        <label className="veld"><span>Mobiel</span><input value={f.mobiel} onChange={(e) => zet({ ...f, mobiel: e.target.value })} inputMode="tel" autoComplete="off" placeholder="06 12345678" /></label>
        <label className="veld"><span>Rol</span>
          <select value={f.rol} onChange={(e) => zet({ ...f, rol: e.target.value as Rol })} disabled={zelf}>
            {rollen.map((r) => <option key={r} value={r}>{ROLNAAM[r]}</option>)}
          </select>
        </label>
        {kanInloggen(f.rol) && (
          <label className="veld"><span>E-mail (om in te loggen)</span><input type="email" inputMode="email" value={f.email} onChange={(e) => zet({ ...f, email: e.target.value })} autoComplete="off" required /></label>
        )}
        {heeftPin(f.rol) && (
          <label className="veld"><span>PIN</span>
            <input type="password" inputMode="numeric" pattern={`[0-9]{${lengte}}`} maxLength={lengte} value={f.pin} autoComplete="new-password"
              onChange={(e) => zet({ ...f, pin: e.target.value.replace(/\D/g, '') })} placeholder={'•'.repeat(lengte)} required={!p.heeftPin && f.rol === 'medewerker'} />
            <small>{p.heeftPin ? 'Laat leeg om de huidige PIN te houden. ' : `${lengte} cijfers. `}Dezelfde PIN als voor inklokken; uniek binnen het bedrijf.</small>
          </label>
        )}
      </div>
      {!alleVestigingen(f.rol) && f.rol !== 'marketing' ? (
        <div className="veld"><span>Vestigingen</span>
          <div className="keuzes">
            {s.vestigingen.filter((v) => v.actief || vest.includes(v.id)).map((v) => (
              <label key={v.id} className="keuze">
                <input type="checkbox" checked={vest.includes(v.id)} onChange={(e) => zetVest(e.target.checked ? [...vest, v.id] : vest.filter((x) => x !== v.id))} />
                {v.naam.replace(/^Truckwash\s*/i, '')}
              </label>
            ))}
          </div>
        </div>
      ) : <p className="stil klein">{f.rol === 'marketing' ? 'Marketing werkt niet op een vestiging en heeft geen PIN.' : 'Deze rol werkt op alle vestigingen.'}</p>}
      {fout && <div className="melding fout">{fout}</div>}
      <div className="knoppenrij">
        {p.id && !zelf && (actief
          ? <button type="button" className="knop gevaar" disabled={bezig} onClick={() => { if (confirm(`${p.naam} uit dienst zetten? De PIN en de inlog werken dan meteen niet meer.`)) { zetActief(false); opslaan(false) } }}><UserX size={18} />Uit dienst</button>
          : <button type="button" className="knop" disabled={bezig} onClick={() => { zetActief(true); opslaan(true) }}>Weer in dienst</button>)}
        {p.id && kanInloggen(f.rol) && p.actief && (
          <button type="button" className="knop" onClick={() => uitnodigen(p as Ik)}><KeyRound size={18} />{p.heeftWachtwoord ? 'Nieuwe wachtwoordlink' : 'Uitnodigingslink'}</button>
        )}
        <button className="knop geel" disabled={bezig}>{bezig ? 'Opslaan…' : 'Opslaan'}</button>
      </div>
      {!p.id && kanInloggen(f.rol) && <p className="stil klein">Na het opslaan maak je een uitnodigingslink waarmee {f.naam || 'deze persoon'} zelf een wachtwoord kiest.</p>}
    </form>
  )
}

function Uitnodiging({ link, naam, verloopt }: { link: string; naam: string; verloopt: string }) {
  const [gekopieerd, zet] = useState(false)
  const tekst = `Hoi ${naam.split(' ')[0]}, met deze link kies je je wachtwoord voor TW1 Club beheer: ${link}`
  return (
    <div className="lijst" style={{ gap: 14 }}>
      <p>Stuur deze link naar {naam}. Hij werkt één keer en is geldig tot {datum(verloopt)}.</p>
      <input className="invoer" readOnly value={link} onFocus={(e) => e.target.select()} />
      <div className="route-knoppen">
        <button className="knop" onClick={async () => { await navigator.clipboard?.writeText(link); zet(true) }}><Copy size={18} />{gekopieerd ? 'Gekopieerd' : 'Kopiëren'}</button>
        {'share' in navigator
          ? <button className="knop geel" onClick={() => navigator.share({ text: tekst }).catch(() => {})}><Send size={18} />Delen</button>
          : <a className="knop geel" href={`https://wa.me/?text=${encodeURIComponent(tekst)}`} target="_blank" rel="noreferrer"><Send size={18} />WhatsApp</a>}
      </div>
    </div>
  )
}

